/**
 * EventShareButton
 *
 * Share button for events.
 * Uses Web Share API if available, otherwise copies link to clipboard.
 */

import React, { useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { useToastStore } from '../../stores/useToastStore';
import { getEventUrl } from '../../utils/publicUrl';

const EventShareButton = ({ event, calendar }) => {
  const [isCopied, setIsCopied] = useState(false);
  const addToast = useToastStore((state) => state.addToast);

  const getShareUrl = () => {
    const sharedEventUrl = getEventUrl(event);
    if (sharedEventUrl) return sharedEventUrl;
    // Fallback URL for themed-calendar events that are not shared events
    const baseUrl = window.location.origin;
    return `${baseUrl}/calendars/${calendar.id}?event=${event.id || encodeURIComponent(event.name)}`;
  };

  const getShareText = () => {
    const dateStr = event.startDate || event.date;
    return `${event.name} - ${event.location || ''} ${dateStr ? `(${dateStr})` : ''}`.trim();
  };

  const handleShare = async () => {
    const shareUrl = getShareUrl();
    const shareText = getShareText();

    // Try native share first
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.name,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        if (err.name === 'AbortError') return;
      }
    }

    // Fall back to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);

      addToast({
        type: 'success',
        message: 'Link copied to clipboard!',
        duration: 2000,
      });

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      addToast({
        type: 'error',
        message: 'Failed to copy link',
        duration: 2000,
      });
    }
  };

  return (
    <button
      className="event-share-button"
      onClick={handleShare}
      aria-label="Share event"
    >
      <Icon name={isCopied ? 'check' : 'share alternate'} />
      <span>{isCopied ? 'Copied!' : 'Share'}</span>
    </button>
  );
};

export default EventShareButton;
